"use client";

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import * as Sentry from "@sentry/react";
import {
  ChevronDown,
  Columns2,
  Download,
  FileText,
  History,
  PanelLeftOpen,
  RotateCcw,
  X
} from "lucide-react";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle
} from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { Spinner } from "@/components/ui/spinner";
import { StatusBadge } from "@/components/ui/status-badge";
import { cn } from "@/lib/utils";
import { DraftVersion } from "../../../api/types/draft";
import { getDraftDocx, getDraftVersionDocx } from "../../../api/drafts-v2";
import {
  DraftDocxEditor,
  type DraftDocxEditorProps
} from "../../draftEditor/DraftDocxEditor";
import {
  getDisplayName,
  getVersionSubtitle,
  showsPersonAvatar
} from "../versionLabels";
import {
  diffLines,
  diffSummary,
  docxToText,
  downloadDocx,
  type DiffLine
} from "../../draftEditor/docxExport";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  draftId: string | null;
  draftTitle: string;
  versions: DraftVersion[];
  isLoading: boolean;
  isLoadingMore: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  error: string | null;
  restoringVersionId: string | null;
  onRestoreClick: (version: DraftVersion) => void;
  /** Overlay for the preview editor (see `DraftDocxEditor.overlay`). */
  editorOverlay?: DraftDocxEditorProps["overlay"];
  /** The live document's bytes for "Compare with current"; server copy otherwise. */
  currentBytes?: () => Promise<ArrayBuffer | null>;
  /** Text on the per-version action. */
  restoreLabel?: string;
};

type Comparison =
  | { state: "loading" }
  | { state: "error"; message: string }
  | { state: "ready"; lines: DiffLine[]; added: number; removed: number };

const toDateMs = (value: unknown): number | null => {
  if (value == null) return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value === "string") {
    const ms = Date.parse(value);
    return Number.isFinite(ms) ? ms : null;
  }
  if (value instanceof Date) return value.getTime();

  // Firestore Timestamp-like objects sometimes provide toDate()
  if (
    typeof value === "object" &&
    value !== null &&
    "toDate" in value &&
    typeof (value as { toDate?: unknown }).toDate === "function"
  ) {
    try {
      const d = (value as { toDate: () => unknown }).toDate();
      if (d instanceof Date) return d.getTime();
    } catch {
      // ignore
    }
  }
  return null;
};

/** When a version's text is from: its session's last save, else its creation. */
const versionTime = (version: DraftVersion): string =>
  version.updated_at ?? version.created_at;

const formatRelativeTime = (value: unknown): string => {
  const ts = toDateMs(value);
  if (!ts) return "";

  const diffMsRaw = Date.now() - ts;
  const diffMs = Math.max(0, diffMsRaw);
  // Only show relative time for very recent events (<= 10 minutes).
  if (diffMs > 10 * 60_000) {
    try {
      return new Date(ts).toLocaleString();
    } catch {
      return "";
    }
  }

  if (diffMs < 60_000) {
    const diffSec = Math.max(1, Math.floor(diffMs / 1000));
    return `${diffSec} sec${diffSec === 1 ? "" : "s"} ago`;
  }

  const diffMin = Math.floor(diffMs / 60_000);
  return `${diffMin} min${diffMin === 1 ? "" : "s"} ago`;
};

const versionFilename = (title: string, version: DraftVersion): string => {
  const stamp = toDateMs(versionTime(version));
  const when = stamp
    ? new Date(stamp)
        .toISOString()
        .slice(0, 16)
        .replace("T", " ")
        .replace(":", "")
    : version.version_id;
  const base = (title || "Draft").replace(/[\\/:*?"<>|]+/g, "-").trim();
  return `${base} (${when}).docx`;
};

/** Read-only, line-level view of what changed between a version and now. */
const VersionComparison = ({ comparison }: { comparison: Comparison }) => {
  if (comparison.state === "loading") {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <span className="text-base text-fg-3">Comparing…</span>
      </div>
    );
  }
  if (comparison.state === "error") {
    return (
      <div className="flex h-full w-full items-center justify-center px-6 text-center">
        <span className="text-base text-danger">{comparison.message}</span>
      </div>
    );
  }
  if (comparison.added === 0 && comparison.removed === 0) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <span className="text-base text-fg-3">
          The text of this version matches the current document.
        </span>
      </div>
    );
  }
  return (
    <div
      className="h-full w-full overflow-auto px-6 py-4 font-mono text-base leading-6"
      data-testid="version-comparison"
    >
      <div className="pb-3 text-sm text-fg-3">
        {comparison.added} line{comparison.added === 1 ? "" : "s"} added,{" "}
        {comparison.removed} line{comparison.removed === 1 ? "" : "s"} removed
        (text only; formatting is not compared)
      </div>
      {comparison.lines.map((line, index) => (
        <div
          // eslint-disable-next-line react/no-array-index-key
          key={index}
          className={cn(
            "rounded-xs px-2 break-words whitespace-pre-wrap",
            line.kind === "added"
              ? "bg-success-soft text-success"
              : line.kind === "removed"
              ? "bg-danger-soft text-danger line-through"
              : "text-fg"
          )}
        >
          {line.kind === "added" ? "+ " : line.kind === "removed" ? "− " : "  "}
          {line.text || " "}
        </div>
      ))}
    </div>
  );
};

const getAvatarInitial = (name: string): string => {
  const trimmed = name.trim();
  if (!trimmed) return "?";
  return trimmed[0].toUpperCase();
};

export function DraftVersionHistoryDrawer({
  open,
  onOpenChange,
  draftId,
  draftTitle,
  versions,
  isLoading,
  isLoadingMore,
  hasMore,
  onLoadMore,
  error,
  restoringVersionId,
  onRestoreClick,
  editorOverlay,
  currentBytes,
  restoreLabel = "Restore"
}: Props) {
  const sortedVersions = useMemo(() => {
    const copy = [...versions];
    copy.sort((a, b) => {
      const aMs = toDateMs(a.created_at);
      const bMs = toDateMs(b.created_at);
      if (aMs != null && bMs != null) return bMs - aMs;
      if (aMs != null) return -1;
      if (bMs != null) return 1;
      return a.version_id.localeCompare(b.version_id);
    });
    return copy;
  }, [versions]);

  const currentVersionId = sortedVersions[0]?.version_id ?? null;

  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(
    null
  );
  const isPreviewOpen = Boolean(selectedVersionId);
  const [view, setView] = useState<"preview" | "compare">("preview");
  const [comparison, setComparison] = useState<Comparison | null>(null);
  const [downloadingVersionId, setDownloadingVersionId] = useState<
    string | null
  >(null);

  const selectVersion = useCallback((versionId: string | null) => {
    setSelectedVersionId(versionId);
    setView("preview");
    setComparison(null);
  }, []);

  const downloadVersion = useCallback(
    async (version: DraftVersion) => {
      if (!draftId) return;
      setDownloadingVersionId(version.version_id);
      try {
        const { bytes } = await getDraftVersionDocx(
          draftId,
          version.version_id
        );
        downloadDocx(bytes, versionFilename(draftTitle, version));
      } catch (e) {
        Sentry.captureException(e);
      } finally {
        setDownloadingVersionId(null);
      }
    },
    [draftId, draftTitle]
  );

  const compareVersion = useCallback(
    async (version: DraftVersion) => {
      if (!draftId) return;
      setView("compare");
      setComparison({ state: "loading" });
      try {
        const [older, current] = await Promise.all([
          getDraftVersionDocx(draftId, version.version_id).then((r) => r.bytes),
          (currentBytes ? currentBytes() : Promise.resolve(null)).then(
            (live) => live ?? getDraftDocx(draftId).then((r) => r.bytes)
          )
        ]);
        const [before, after] = await Promise.all([
          docxToText(older),
          docxToText(current)
        ]);
        const lines = diffLines(before, after);
        setComparison({ state: "ready", lines, ...diffSummary(lines) });
      } catch (e) {
        Sentry.captureException(e);
        setComparison({
          state: "error",
          message: "Couldn't compare this version. Please try again."
        });
      }
    },
    [currentBytes, draftId]
  );

  const versionsScrollRef = useRef<HTMLDivElement | null>(null);
  const [showVersionsScrollHint, setShowVersionsScrollHint] = useState(false);
  const versionsHintRafRef = useRef<number | null>(null);

  const updateVersionsScrollHint = useCallback(() => {
    const el = versionsScrollRef.current;
    if (!el) return;
    const canScroll = el.scrollHeight - el.clientHeight > 8;
    const atTop = el.scrollTop <= 6;
    const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 6;
    setShowVersionsScrollHint(canScroll && atTop && !atBottom);
  }, []);

  useEffect(() => {
    if (!open) return () => undefined;

    if (versionsHintRafRef.current) {
      window.cancelAnimationFrame(versionsHintRafRef.current);
      versionsHintRafRef.current = null;
    }

    const startedAt = window.performance?.now?.() ?? Date.now();
    const maxMs = 2000;

    const tick = () => {
      updateVersionsScrollHint();
      const now = window.performance?.now?.() ?? Date.now();
      if (now - startedAt < maxMs) {
        versionsHintRafRef.current = window.requestAnimationFrame(tick);
      } else {
        versionsHintRafRef.current = null;
      }
    };

    versionsHintRafRef.current = window.requestAnimationFrame(tick);
    window.addEventListener("resize", updateVersionsScrollHint);

    return () => {
      if (versionsHintRafRef.current) {
        window.cancelAnimationFrame(versionsHintRafRef.current);
        versionsHintRafRef.current = null;
      }
      window.removeEventListener("resize", updateVersionsScrollHint);
    };
  }, [open, isLoading, sortedVersions.length, updateVersionsScrollHint]);

  const selectedVersion = useMemo(() => {
    if (!selectedVersionId) return null;
    return (
      sortedVersions.find((v) => v.version_id === selectedVersionId) ?? null
    );
  }, [sortedVersions, selectedVersionId]);

  const viewingAtLabel = useMemo(() => {
    if (!selectedVersion) return "";
    try {
      const dt = new Date(versionTime(selectedVersion));
      const text = dt.toLocaleString();
      return text;
    } catch {
      return versionTime(selectedVersion);
    }
  }, [selectedVersion]);

  const viewingDraftName = useMemo(() => {
    const t = (draftTitle ?? "").trim();
    if (t) return t;
    const fromVersion = (selectedVersion?.draft_payload?.title ?? "").trim();
    return fromVersion || "Draft";
  }, [draftTitle, selectedVersion]);

  useEffect(() => {
    if (open) return;
    // Reset selection when closing.
    selectVersion(null);
  }, [open, selectVersion]);

  return (
    <Sheet
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onOpenChange(false);
      }}
    >
      <SheetContent
        side="right"
        showCloseButton={false}
        overlayClassName="z-2147483647"
        className="inset-0 z-2147483647 h-screen max-h-screen min-w-0 flex-row items-stretch justify-end gap-1.5 bg-transparent p-2 shadow-none data-[side=right]:w-screen data-[side=right]:border-l-0 data-[side=right]:sm:max-w-none"
        onPointerDown={(e) => {
          // Prevent bubbling through portals to underlying page.
          e.stopPropagation();
          // When no preview is open, clicking the dimmed area (outside the
          // panels) closes the drawer.
          if (!isPreviewOpen && e.target === e.currentTarget) {
            onOpenChange(false);
          }
        }}
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        {/* Left: version preview */}
        {isPreviewOpen ? (
          <div className="hidden h-full w-205 max-w-11/20 min-w-0 flex-none items-stretch md:flex">
            <div className="h-full w-full min-w-0 overflow-hidden rounded-xl bg-surface shadow-dialog ring-1 ring-line-2">
              <div className="relative flex h-full min-h-0 w-full min-w-0 flex-col">
                <div className="absolute top-4 right-4 z-20 flex items-center gap-2">
                  {selectedVersion ? (
                    <>
                      <Button
                        variant="secondary"
                        className="bg-elevated"
                        onClick={() => {
                          if (view === "compare") {
                            setView("preview");
                          } else {
                            void compareVersion(selectedVersion);
                          }
                        }}
                      >
                        {view === "compare" ? <FileText /> : <Columns2 />}
                        {view === "compare"
                          ? "Show Document"
                          : "Compare With Current"}
                      </Button>
                      <Button
                        variant="secondary"
                        className="bg-elevated"
                        disabled={
                          downloadingVersionId === selectedVersion.version_id
                        }
                        onClick={() => void downloadVersion(selectedVersion)}
                      >
                        {downloadingVersionId === selectedVersion.version_id ? (
                          <Spinner className="size-3.5" />
                        ) : (
                          <Download />
                        )}
                        Download
                      </Button>
                    </>
                  ) : null}
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label="Close Preview"
                    onClick={() => selectVersion(null)}
                  >
                    <X />
                  </Button>
                </div>
                {viewingAtLabel ? (
                  <div className="pointer-events-none absolute top-4 left-6 z-20">
                    <div className="flex items-center gap-2 rounded-md bg-elevated px-3 py-2 shadow-popover ring-1 ring-line-3">
                      <span className="text-sm font-medium text-fg">
                        Viewing {viewingDraftName} at {viewingAtLabel}
                      </span>
                    </div>
                  </div>
                ) : null}
                <div className="min-h-0 w-full min-w-0 flex-1 overflow-hidden">
                  {view === "compare" && comparison ? (
                    <div className="h-full w-full bg-surface pt-16">
                      <VersionComparison comparison={comparison} />
                    </div>
                  ) : draftId && selectedVersionId ? (
                    <DraftDocxEditor
                      key={selectedVersionId}
                      draftId={draftId}
                      title={draftTitle}
                      readOnly
                      loadDocx={() =>
                        getDraftVersionDocx(draftId, selectedVersionId)
                      }
                      overlay={editorOverlay}
                    />
                  ) : (
                    <div className="flex h-full w-full flex-col items-center justify-center gap-2 px-4 text-center">
                      <span className="text-base text-fg-3">
                        No file available to preview.
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : null}

        {/* Right: version list */}
        <div className="relative h-full w-110 max-w-full overflow-hidden rounded-xl bg-elevated shadow-dialog ring-1 ring-line-2">
          <div className="flex h-full w-full flex-col">
            <div className="flex w-full items-center gap-3 border-b border-line-2 px-4 py-3">
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label="Close Version History"
                onClick={() => onOpenChange(false)}
              >
                <PanelLeftOpen />
              </Button>
              <div className="flex min-w-0 flex-col">
                <SheetTitle className="truncate">Version History</SheetTitle>
                <SheetDescription className="truncate text-sm">
                  View and restore previous versions of this draft.
                </SheetDescription>
              </div>
            </div>

            <div
              ref={versionsScrollRef}
              className="flex w-full shrink-0 grow basis-0 flex-col overflow-y-auto"
              onScroll={updateVersionsScrollHint}
            >
              {isLoading ? (
                <div className="flex w-full flex-col divide-y divide-line-1">
                  {Array.from({ length: 6 }).map((_, idx) => (
                    <div
                      // eslint-disable-next-line react/no-array-index-key
                      key={`version_skel_${idx}`}
                      className="flex w-full flex-col px-6 py-4"
                    >
                      <div className="flex w-full items-center justify-between">
                        <div className="flex items-center gap-2">
                          {idx === 0 ? <Skeleton className="h-3 w-24" /> : null}
                          <Skeleton className="h-3 w-18" />
                        </div>
                        <Skeleton className="h-3 w-21" />
                      </div>
                      <div className="flex w-full items-center gap-3 pt-3">
                        <Skeleton className="size-6 rounded-md" />
                        <div className="flex w-full flex-col gap-2">
                          <Skeleton className="h-4 w-11/20" />
                          <Skeleton className="h-3 w-3/4" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : error ? (
                <div className="flex w-full flex-col px-6 py-4">
                  <span className="text-base text-danger">{error}</span>
                </div>
              ) : sortedVersions.length === 0 ? (
                <EmptyState
                  className="flex-1 px-6 py-10"
                  icon={History}
                  title="No Version History"
                  description="Version history will appear here as you regenerate or edit this draft."
                />
              ) : (
                <div className="flex w-full flex-col divide-y divide-line-1">
                  {sortedVersions.map((v, index) => {
                    const name = getDisplayName(v);
                    const initial = getAvatarInitial(name);
                    const timeLabel = formatRelativeTime(versionTime(v));
                    const isCurrent = index === 0;
                    const isSelected = selectedVersionId === v.version_id;
                    const message = getVersionSubtitle(v);
                    const avatarUrl = showsPersonAvatar(v)
                      ? v.created_by_profile?.avatar_data_url ?? undefined
                      : undefined;

                    const isRestoring = restoringVersionId === v.version_id;
                    const canRestore =
                      Boolean(v.version_id) && !isRestoring && !isCurrent;

                    return (
                      <div
                        key={v.version_id}
                        className={cn(
                          "group flex w-full cursor-pointer flex-col px-6 py-4 transition-colors duration-100 hover:bg-fill-1",
                          isSelected && "bg-fill-2 hover:bg-fill-2"
                        )}
                        onClick={() => selectVersion(v.version_id)}
                      >
                        <div className="flex w-full items-center justify-between">
                          <div className="flex items-center gap-2">
                            {isCurrent ? (
                              <StatusBadge tone="accent">
                                Current Version
                              </StatusBadge>
                            ) : isPreviewOpen && isSelected ? (
                              <StatusBadge tone="neutral" dot>
                                Now Viewing
                              </StatusBadge>
                            ) : null}
                            {timeLabel ? (
                              <span className="text-sm text-fg-3">
                                {timeLabel}
                              </span>
                            ) : null}
                          </div>

                          {isCurrent ? null : (
                            <Button
                              className={cn(
                                "transition-opacity",
                                isSelected
                                  ? "opacity-100"
                                  : "opacity-0 group-hover:opacity-100"
                              )}
                              variant="secondary"
                              disabled={!canRestore}
                              onClick={(e) => {
                                e.stopPropagation();
                                onRestoreClick(v);
                              }}
                            >
                              {isRestoring ? (
                                <Spinner className="size-3.5" />
                              ) : (
                                <RotateCcw />
                              )}
                              {restoreLabel}
                            </Button>
                          )}
                        </div>

                        <div className="flex w-full items-center justify-between pt-2">
                          <div className="flex items-center gap-3">
                            <Avatar size="sm">
                              {avatarUrl ? (
                                <AvatarImage src={avatarUrl} alt="" />
                              ) : null}
                              <AvatarFallback>{initial}</AvatarFallback>
                            </Avatar>
                            <div className="flex flex-col">
                              <span className="text-base font-medium text-fg">
                                {name}
                              </span>
                              <span className="text-sm text-fg-3">
                                {message}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {hasMore && !isLoading && !error ? (
                <div className="flex min-h-16 w-full items-center justify-center border-t border-line-1 px-6 py-4">
                  <Button
                    variant="secondary"
                    disabled={isLoadingMore}
                    onClick={(e) => {
                      e.stopPropagation();
                      onLoadMore();
                    }}
                  >
                    {isLoadingMore ? <Spinner className="size-3.5" /> : null}
                    Load More
                  </Button>
                </div>
              ) : null}
            </div>

            {showVersionsScrollHint ? (
              <div className="pointer-events-none absolute bottom-3 left-1/2 z-20 -translate-x-1/2">
                <div className="flex size-8 animate-bounce items-center justify-center rounded-full bg-elevated opacity-90 shadow-popover ring-1 ring-line-2 motion-reduce:animate-none">
                  <ChevronDown className="size-4 text-fg" />
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
